// The panel-change request (D17, CR-075, CR-074): a SlideOver titled Panel change that names the round, the person
// leaving it and their Replacement, lets the round's ROOTS values move (its skills are shown), and asks the server — as the choice changes — whether
// the change is material and which SLA tier it falls in (`panel.preview_change`). Submit creates the request
// (`panel.request_change`, a Draft) and submits it (`panel.submit_change`); the server routes it to Pending Recruiter
// Review or Blocked. Nothing here decides material or the tier: both are the preview's answer, never the browser's.
//
// The form itself is `PanelChangeForm` (props in, events out, no reads), so the SSR suite renders it from fixtures;
// `PanelChangeSlideOver` wraps it with the reads and writes.

import { useEffect, useMemo, useState } from "react";
import {
  Chip, Chips, ErrorStrip, Field, KeyValue, Pill, PersonPick, Select, SlideOver, TextArea,
  COMMON_LABELS, G1_LABELS, LABEL_TEMPLATES, ROOTS_LABELS, SLA_LABELS, SLIDE_LABELS, ACTION_LABELS, SKILL_N,
  type PersonOption, type RootsValue, type Tone,
} from "@/mc";
import { intakeOpen, panelPreviewChange, panelRequestChange, panelSubmitChange, type SeatChangeIn } from "@/api/tools";
import { useWrite, errorText } from "@/api/useTool";
import { useSettledRead } from "../shared/settledRead";
import { CHANGE_LABELS } from "./labels";
import "./change.css";

/** The three tiers of D9, in the server's words (`panel.preview_change.sla_tier`). */
export type SlaTier = "standard" | "expedited" | "emergency";

const SLA_WORD: Readonly<Record<SlaTier, string>> = {
  standard: SLA_LABELS.standardSla,
  expedited: SLA_LABELS.expeditedSla,
  emergency: SLA_LABELS.emergency,
};
const SLA_TONE: Readonly<Record<SlaTier, Tone>> = { standard: "neutral", expedited: "flag", emergency: "red" };

/** The SLA pill for a tier the server named; nothing for an unknown or absent tier. */
export function SlaPill({ tier, hs }: { tier: string | null | undefined; hs?: string | undefined }) {
  if (!tier || !(tier in SLA_WORD)) return null;
  const t = tier as SlaTier;
  return <Pill tone={SLA_TONE[t]} hs={hs}>{SLA_WORD[t]}</Pill>;
}

/** One revalidation check (S07: `revalidation.checks[]`). */
export interface RevalidationCheck { key: string; passed: boolean; gaps: readonly string[] }
/** `panel.preview_change`'s answer. */
export interface ChangePreview {
  material: boolean;
  material_reasons: readonly string[];
  kind: string;
  sla_tier: SlaTier | string;
  sla_due_at: string | null;
  revalidation: { ok: boolean; checks: readonly RevalidationCheck[] };
}

/** A round as the form needs it: from `intake.open` rounds. */
export interface ChangeRound {
  id: string;
  /** The round's position in the Final Interview Loop. */
  sequence: number;
  values: readonly string[];
  skills: readonly number[];
  /** The seated primary interviewer. */
  current: { id: string; name: string } | null;
}

const ROOTS_KEYS: readonly RootsValue[] = ["respect", "ownership", "opportunity", "together", "service"];
const valueWord = (value: string) => (ROOTS_KEYS.includes(value as RootsValue) ? ROOTS_LABELS[value as RootsValue] : value);

export interface PanelChangeFormProps {
  rounds: readonly ChangeRound[];
  roundId: string;
  onRound?: ((id: string) => void) | undefined;
  /** The round is fixed by the caller (a row's Panel change): no round picker. */
  roundFixed?: boolean | undefined;
  people: readonly PersonOption[];
  replacement: string;
  onReplacement?: ((id: string) => void) | undefined;
  reason: string;
  onReason?: ((text: string) => void) | undefined;
  preview?: ChangePreview | null | undefined;
  previewLoading?: boolean | undefined;
  /** The round's ROOTS values as the change would leave them; with `onValues` they are toggles. */
  values?: readonly string[] | undefined;
  onValues?: ((values: string[]) => void) | undefined;
}

/** The slide-over's body: round, Replacement, the ROOTS values (toggles when the caller can move them), the material pill, Reason and the SLA pill. */
export function PanelChangeForm({
  rounds, roundId, onRound, roundFixed, people, replacement, onReplacement, reason, onReason, preview, previewLoading,
  values, onValues,
}: PanelChangeFormProps) {
  const round = rounds.find((r) => r.id === roundId) ?? null;
  const roundName = (r: ChangeRound) => LABEL_TEMPLATES.finalLoopRound({ n: r.sequence });
  const options = people.filter((p) => p.value !== round?.current?.id);
  return (
    <div className="pchg-form">
      {roundFixed || rounds.length <= 1 ? (
        round ? <KeyValue cols={1} items={[[CHANGE_LABELS.round, roundName(round)]]} /> : null
      ) : (
        <Field label={CHANGE_LABELS.round} required>
          <Select
            options={rounds.map((r) => ({ value: r.id, label: roundName(r) }))}
            value={roundId}
            onChange={(value) => onRound?.(value)}
            
          />
        </Field>
      )}
      {round ? (
        <>
          <Field label={G1_LABELS.replacement} required={!onValues}>
            <div className="pchg-swap">
              <span className="pchg-from">{round.current?.name ?? "—"}</span>
              <span className="pchg-arrow" aria-hidden="true">→</span>
              <PersonPick
                options={options}
                value={replacement || undefined}
                onChange={(value) => onReplacement?.(value)}
                label={G1_LABELS.replacement}
                required={!onValues}
                
              />
            </div>
          </Field>
          <Chips label={ROOTS_LABELS.roots}>
            {onValues ? ROOTS_KEYS.map((value) => {
              const held = values ?? round.values;
              return (
                <Chip key={value} sm on={held.includes(value)} 
                  onToggle={(on) => onValues(on ? [...held, value] : held.filter((v) => v !== value))}>
                  {valueWord(value)}
                </Chip>
              );
            }) : round.values.map((value) => <Chip key={value} sm mode="view">{valueWord(value)}</Chip>)}
            {round.skills.map((n) => <Chip key={`s${n}`} sm mode="view">{SKILL_N({ n })}</Chip>)}
          </Chips>
        </>
      ) : null}
      <div className="pchg-pills" aria-live="polite" aria-busy={previewLoading || undefined}>
        {preview?.material ? <Pill tone="flag" >{G1_LABELS.materialChange}</Pill> : null}
        <SlaPill tier={preview?.sla_tier}  />
      </div>
      <Field label={SLIDE_LABELS.reason} required>
        <TextArea rows={3} maxLength={1000} value={reason} onChange={(event) => onReason?.(event.target.value)}  />
      </Field>
    </div>
  );
}

/** Reads `intake.open`'s rounds into the form's shape: the Final Interview Loop rounds in use. */
export function roundsOf(rounds: readonly { id: string; kind: string; sequence: number; in_use: boolean; values: string[]; skill_numbers: number[]; primary: Record<string, unknown> | null; backup: Record<string, unknown> | null }[]): ChangeRound[] {
  return rounds
    .filter((r) => r.kind === "onsite" && r.in_use)
    .map((r) => ({
      id: r.id,
      sequence: r.sequence,
      values: r.values,
      skills: r.skill_numbers,
      current: r.primary?.person_id ? { id: String(r.primary.person_id), name: String(r.primary.name ?? r.primary.person_id) } : null,
    }));
}

/** Everybody seated on the requisition's rounds: the people this screen can name (V1 has no roster read). */
export function peopleOf(rounds: readonly { primary: Record<string, unknown> | null; backup: Record<string, unknown> | null }[]): PersonOption[] {
  const byId = new Map<string, string>();
  for (const r of rounds) {
    for (const seat of [r.primary, r.backup]) {
      if (seat?.person_id) byId.set(String(seat.person_id), String(seat.name ?? seat.person_id));
    }
  }
  return [...byId].map(([value, name]) => ({ value, name }));
}

export interface PanelChangeSlideOverProps {
  requisitionId: string;
  roundDefId?: string | undefined;
  /** The declined acknowledgment the change answers (an Acknowledgment summary row's Panel change). */
  acknowledgmentId?: string | undefined;
  open: boolean;
  onClose: () => void;
  onSubmitted: (requestId: string) => void;
}

export function PanelChangeSlideOver({ requisitionId, roundDefId, acknowledgmentId, open, onClose, onSubmitted }: PanelChangeSlideOverProps) {
  const intake = useSettledRead(["intake", requisitionId], () => intakeOpen({ requisition_id: requisitionId }), { enabled: open });
  const rounds = useMemo(() => roundsOf(intake.data?.rounds ?? []), [intake.data]);
  const people = useMemo(() => peopleOf(intake.data?.rounds ?? []), [intake.data]);
  const [roundId, setRoundId] = useState(roundDefId ?? "");
  const [replacement, setReplacement] = useState("");
  const [reason, setReason] = useState("");
  const [values, setValues] = useState<string[] | null>(null);
  const [typed, setTyped] = useState({ roundId: "", replacement: "", values: "" });

  useEffect(() => {
    if (open) { setRoundId(roundDefId ?? ""); setReplacement(""); setReason(""); setValues(null); }
  }, [open, roundDefId]);
  useEffect(() => {
    if (!roundId && rounds[0]) setRoundId(rounds[0].id);
  }, [roundId, rounds]);
  // The preview follows the choice a moment after it settles, so a fast pick does not send a read per keystroke.
  const round = rounds.find((r) => r.id === roundId);
  // The values as a change: none until they differ from the round's own (order does not count).
  const moved = values && round && [...values].sort().join() !== [...round.values].sort().join() ? ROOTS_KEYS.filter((v) => values.includes(v)) : null;
  const movedKey = moved ? moved.join() : "";
  useEffect(() => {
    const timer = setTimeout(() => setTyped({ roundId, replacement, values: movedKey }), 250);
    return () => clearTimeout(timer);
  }, [roundId, replacement, movedKey]);

  // Nothing is previewed until something changes: an empty change has no tier and is not material.
  const change = (rd: string, to: string, vals: string) => ({
    seat_changes: to ? [{ round_def_id: rd, seat: "primary", to_person_id: to }] as SeatChangeIn[] : [],
    round_changes: vals ? [{ round_def_id: rd, values: vals.split(",") as RootsValue[] }] : [],
  });
  const preview = useSettledRead(
    ["panel-change-preview", requisitionId, typed.roundId, typed.replacement, typed.values],
    () => panelPreviewChange({ requisition_id: requisitionId, ...change(typed.roundId, typed.replacement, typed.values) }) as Promise<ChangePreview>,
    { enabled: open && !!typed.roundId && !!(typed.replacement || typed.values), staleTime: 0 },
  );

  const submit = useWrite(async (input: { reason: string; to: string; rd: string; values: string }, key: string) => {
    const draft = await panelRequestChange({
      requisition_id: requisitionId,
      ...(acknowledgmentId ? { acknowledgment_id: acknowledgmentId } : {}),
      ...change(input.rd, input.to, input.values),
      reason: input.reason,
    }, `${key}:request`);
    await panelSubmitChange({ request_id: draft.request_id, expected_version: draft.version }, `${key}:submit`);
    return draft.request_id as string;
  }, [["panel-change"], ["packets"], ["intake", requisitionId], ["today"]]);

  const trimmed = reason.trim();
  const blocked = !round || !(replacement || moved) || trimmed.length < 3 ? { reason: COMMON_LABELS.required } : undefined;
  return (
    <SlideOver
      title={SLIDE_LABELS.panelChange}
      open={open}
      onClose={onClose}
      dismissLabel={ACTION_LABELS.cancel}
      
      primary={{
        label: ACTION_LABELS.submit,
        blocked,
        busy: submit.isPending,
        hs: "pchg.submit",
        onClick: () => {
          if (blocked) return;
          void submit.mutateAsync({ reason: trimmed, to: replacement, rd: roundId, values: movedKey }).then(onSubmitted).catch(() => undefined);
        },
      }}
    >
      {intake.error ? <ErrorStrip message={errorText(intake.error)} /> : null}
      <PanelChangeForm
        rounds={rounds}
        roundId={roundId}
        onRound={(id) => { setRoundId(id); setReplacement(""); setValues(null); }}
        roundFixed={!!roundDefId}
        people={people}
        replacement={replacement}
        onReplacement={setReplacement}
        reason={reason}
        onReason={setReason}
        preview={replacement || moved ? preview.data ?? null : null}
        previewLoading={preview.isFetching}
        values={values ?? undefined}
        onValues={setValues}
      />
      {submit.error ? <ErrorStrip message={errorText(submit.error)} /> : null}
    </SlideOver>
  );
}
